import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

const initialState = {
  trades: [],
  processedTrades: {},
  deletes: [],
  loading: false,
  error: null,
};

const tradesSlice = createSlice({
  name: "trades",
  initialState,
  reducers: {
    clearTradesError: (state) => {
      state.error = null;
    },
    setTradesLoading: (state, action) => {
      state.loading = action.payload;
    },
    setTrades: (state, action) => {
      state.trades = action.payload?.filter((row) => {
        return row.deletion == undefined;
      });
    },
    setDeletes: (state, action) => {
      const newDeletedTrades = action.payload?.filter((row) => {
        return row.deletion != undefined;
      });
      state.deletes = newDeletedTrades;
    },
    setHistoricalTrades: (state, action) => {
      const historicalTrades = action.payload?.trades?.filter((row) => {
        return row.deletion == undefined;
      });

      if (historicalTrades && historicalTrades.length > 0) {
        state.trades = [...state.trades, ...historicalTrades];
      }
    },
    addTrades: (state, action) => {
      for (const trade of action.payload) {
        if (trade.deletion && Object.keys(trade.deletion).length > 0) {
          const existingDeleteIndex = state?.deletes?.findIndex(
            (a) => a?.tradeId === trade?.tradeId
          );
          if (existingDeleteIndex !== -1) {
            // Replace existing delete record
            state.deletes[existingDeleteIndex] = trade;
          } else {
            state.deletes = [...state.deletes, trade];
          }
        } else {
          // Add to trades if no deletion data
          const existingTradeIndex = state?.trades?.findIndex(
            (a) => a?.tradeId === trade?.tradeId
          );
          if (existingTradeIndex !== -1) {
            // Replace existing trade
            state.trades[existingTradeIndex] = trade;
          } else {
            state.trades = [...state.trades, trade];
          }
        }
      }
    },
    addOrders: (state, action) => {
      let newOrders = action.payload.data;
      if (action.payload.data.length === 1) {
        const singleOrder = action.payload.data[0];
        let newOrder = state?.trades?.map((a) => {
          if (a?.tradeId === singleOrder.tradeId) {
            return {
              ...a,
              orders:
                a?.orders?.length > 0
                  ? [...a.orders, singleOrder.order]
                  : [singleOrder.order],
            };
          } else {
            return a;
          }
        });

        state.trades = newOrder;
      } else {
        const updatedTrades = state?.trades?.map((trade) => {
          const matchingOrder = newOrders.find(
            (order) => order.tradeId === trade.tradeId
          );
          if (matchingOrder) {
            return {
              ...trade,
              orders:
                trade?.orders?.length > 0
                  ? [...trade.orders, matchingOrder.order]
                  : [matchingOrder.order],
            };
          } else {
            return trade;
          }
        });

        state.trades = updatedTrades;
      }
    },
    addManuals: (state, action) => {
      const newFillEntries = action.payload; // Array of entries with trade & fill

      const updatedTrades = state?.trades?.map((trade) => {
        const matchingFill = newFillEntries.find(
          (entry) => entry.trade === trade.tradeId
        );

        if (matchingFill) {
          return {
            ...trade,
            fill: matchingFill.fill, // Correct usage
          };
        } else {
          return trade;
        }
      });

      state.trades = updatedTrades;
    },
    addManual: (state, action) => {
      let newManual = state?.trades?.map((a) => {
        if (a?.tradeId == action.payload[0]?.trade) {
          return {
            ...a,
            manuals:
              a?.manuals?.length > 0
                ? [...a.manuals, action.payload[0]]
                : [action.payload[0]],
          };
        } else {
          return a;
        }
      });
      state.trades = newManual;
    },
    addFill: (state, action) => {
      const fills = Array.isArray(action.payload)
        ? action.payload
        : action.payload
          ? [action.payload]
          : [];
      let newFill = state?.trades?.map((existingTrade) => {
        const matchingFillData = fills.find(
          (fillObj) => fillObj.trade === existingTrade.tradeId
        );

        if (matchingFillData) {
          // Same shape as addManuals: payload rows are { trade, fill: { shareCount, ... } }
          const fill =
            matchingFillData.fill != null
              ? matchingFillData.fill
              : matchingFillData;
          return { ...existingTrade, fill };
        } else {
          return existingTrade;
        }
      });
      state.trades = newFill;
      toast.success("Fill is Updated");
    },
    addCommission: (state, action) => {
      const newCommissions = action.payload.data;

      if (action.payload.data.length === 1) {
        const singleCommission = action.payload.data[0];

        let newCommission = state?.trades?.map((a) => {
          if (a?.tradeId === singleCommission.trade) {
            return { ...a, commission: singleCommission };
          } else {
            return a;
          }
        });

        state.trades = newCommission;
        toast.success("Trade Commission Updated");
      } else {
        const updatedTrades = state?.trades?.map((trade) => {
          const matchingCommission = newCommissions.find(
            (commission) => commission.trade === trade.tradeId
          );

          if (matchingCommission) {
            return { ...trade, commission: matchingCommission };
          } else {
            return trade;
          }
        });

        state.trades = updatedTrades;
        toast.success("Trade Commissions Updated");
      }
    },
    deleteTrades: (state, action) => {
      const deletedTradeIds = action.payload.data.map(
        (deletedTrade) => deletedTrade.trade
      );
      const newDeletedTrades = state?.trades?.filter(
        (trade) => !deletedTradeIds.includes(trade.tradeId)
      );
      const deletedTrades = state?.trades?.filter((trade) =>
        deletedTradeIds.includes(trade.tradeId)
      );
      state.deletes = [...state.deletes, ...deletedTrades];
      state.trades = newDeletedTrades;
    },
    
    clearTrades: (state) => {
      state.trades = [];
    },
    setProcessedTrades: (state, action) => {
      state.processedTrades = action.payload;
    },
    clearDeletes: (state) => {
      state.deletes = [];
    },

    mergeRouteSsandcTrades: (state, action) => {
      const payload = action.payload;
      const failedTrades = Array.isArray(payload?.failedTrades)
        ? payload.failedTrades
        : [];
      const items = Array.isArray(payload?.updatedTrades)
        ? payload.updatedTrades
        : Array.isArray(payload)
          ? payload
          : [];

      const routedCount = items?.filter((t) => t?.router != null).length;
      const failedCount = failedTrades?.length;

      if (failedCount > 0) {
        toast.error(
          `${failedCount} trade${failedCount === 1 ? "" : "s"} ${
            failedCount === 1 ? "is" : "are"
          } failed to route`
        );
      }
      if (routedCount > 0) {
        toast.success(
          `${routedCount} trade${routedCount === 1 ? "" : "s"} ${
            routedCount === 1 ? "is" : "are"
          } routed succesfuly`
        );
      }

      if (!Array.isArray(items) || items.length === 0) return;

      state.trades = state.trades.map((trade) => {
        const incoming = items.find(
          (t) =>
            t?.tradeId != null &&
            (t.tradeId === trade.tradeId ||
              String(t.tradeId) === String(trade.tradeId))
        );
        if (!incoming || !("router" in incoming)) return trade;
        if (incoming.router == null) return trade;
        return { ...trade, router: incoming.router };
      });
    },

    allocateTrades: (state, action) => {
      const payload = action.payload;
      const failedTrades = Array.isArray(payload?.failedTrades)
        ? payload.failedTrades
        : [];
      const items = Array.isArray(payload?.updatedTrades)
        ? payload.updatedTrades
        : Array.isArray(payload)
          ? payload
          : [];

      const allocatedCount = Array.isArray(items) ? items.length : 0;
      const failedCount = failedTrades.length;

      if (failedCount > 0) {
        toast.error(
          `${failedCount} trade${failedCount === 1 ? "" : "s"} ${
            failedCount === 1 ? "is" : "are"
          } failed to allocate`
        );
      }
      if (allocatedCount > 0) {
        toast.success(
          `${allocatedCount} trade${allocatedCount === 1 ? "" : "s"} ${
            allocatedCount === 1 ? "is" : "are"
          } allocated succesfuly`
        );
      }

      if (!Array.isArray(items) || items.length === 0) return;

      state.trades = state.trades.map((trade) => {
        const incoming = items.find(
          (t) =>
            t?.tradeId != null &&
            (t.tradeId === trade.tradeId ||
              String(t.tradeId) === String(trade.tradeId))
        );
        if (!incoming || !("allocation" in incoming)) return trade;
        return { ...trade, allocation: incoming.allocation };
      });
    },
  },
});

export const {
  clearTradesError,
  setTradesLoading,
  setTrades,
  setHistoricalTrades,
  addTrades,
  addOrders,
  addManuals,
  addManual,
  addFill,
  addCommission,
  deleteTrades,
  updateTrade,
  clearTrades,
  setProcessedTrades,
  setDeletes,
  clearDeletes,
  mergeRouteSsandcTrades,
  allocateTrades,
} = tradesSlice.actions;
export default tradesSlice.reducer;

// Selectors
export const selectTrades = (state) => state.trades.trades;
export const selectProcessedTrades = (state) => state.trades.processedTrades;
export const selectDeletes = (state) => state.trades.deletes;
export const selectTradesLoading = (state) => state.trades.loading;
export const selectTradesError = (state) => state.trades.error;